import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { TenantPriceCategory } from '../types/models/TenantPriceCategory';
import type { ApiResponseTenantPriceCategories } from '../types/api/ApiResponseTenantPriceCategories';

interface UseTenantCategoriesReturn {
  categories: TenantPriceCategory[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
}

// GET /prices/categories — the read-only category lookup, ordered by id (= display order): the
// authoritative section order + names the catalog page groups by. Tenant-readable; useApiFetch injects
// the bearer token. Read-only mirror of GET /admin/products/categories (category CRUD stays under
// /admin), minus the timestamps.
export const useTenantCategories = (enabled = true): UseTenantCategoriesReturn => {
  const [categories, setCategories] = useState<TenantPriceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/prices/categories');
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseTenantPriceCategories | null;
      if (body?.status === 'success' && body.data?.categories) {
        setCategories(body.data.categories);
      } else {
        setError(body?.message ?? 'Failed to load categories');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching categories');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchCategories();
    }
  }, [enabled, auth.isAuthenticated, fetchCategories]);

  return { categories, loading, error, refetch: fetchCategories };
};
